<template>
  <div
    v-if="$store.getters.showSiteLoader"
    id="app"
    class="full-page-loader"
  >
    <AppLoader/>
  </div>

  <div
    v-else
    id="app"
  >
    <AppHeader/>

    <BAlert
      ref="successAlert"
      :show="successDismissCountDown > 0"
      variant="success"
      dismissible
      fade
      @dismissed="successDismissCountDown=0"
    >
      {{ $store.getters.globalSuccessAlertText }}
    </BAlert>

    <BAlert
      ref="errorAlert"
      :show="errorDismissCountDown > 0"
      variant="danger"
      dismissible
      fade
      @dismissed="errorDismissCountDown=0"
    >
      {{ $store.getters.globalErrorAlertText }}
    </BAlert>

    <mfa-approaching-modal ref="mfaModal"/>
    <inactive-timeout-modal/>

    <main>
      <router-view/>
    </main>
    <AppFooter/>
  </div>
</template>

<script>
/* eslint-disable func-names */

import StorageService from '@/api/Storage';
import UserClient from '@/api/UserClient';
import InactiveTimeoutModal from '@/components/modals/InactiveTimeoutModal.vue';
import MfaApproachingModal from '@/components/modals/MfaApproachingModal.vue';
import AppHeader from './components/AppHeader.vue';
import AppFooter from './components/AppFooter.vue';
import AppLoader from './components/AppLoader.vue';
import {BAlert} from "bootstrap-vue-next";

const ALERT_TIMEOUT = 4;

export default {
  name: 'App',
  components: {
    BAlert,
    AppLoader,
    AppHeader,
    AppFooter,
    MfaApproachingModal,
    InactiveTimeoutModal,
  },
  data() {
    return {
      userClient: new UserClient(this.$http),

      successDismissCountDown: 0,
      errorDismissCountDown: 0,
      successInterval: null,
      errorInterval: null,
    };
  },
  watch: {
    '$store.getters.showGlobalSuccessAlert': function (val, oldVal) {
      if (val) {
        this.successDismissCountDown = ALERT_TIMEOUT;
        if (this.successInterval) {
          clearInterval(this.successInterval);
        }
        this.successInterval = setInterval(() => {
          this.successDismissCountDown--
        }, 1000);
        setTimeout(() => {
          this.$refs.successAlert.$el.classList.add('open');
        }, 300);
      } else {
        this.successDismissCountDown = 0;
      }
    },
    '$store.getters.showGlobalErrorAlert': function (val, oldVal) {
      if (val) {
        this.errorDismissCountDown = ALERT_TIMEOUT;

        if (this.errorInterval) {
          clearInterval(this.errorInterval);
        }
        this.errorInterval = setInterval(() => {
          this.errorDismissCountDown--
        }, 1000);
        setTimeout(() => {
          this.$refs.errorAlert.$el.classList.add('open');
        }, 300);
      } else {
        this.errorDismissCountDown = 0;
      }
    },

    // eslint-disable-next-line no-unused-vars
    '$store.getters.mfaWarning': function (val, oldVal) {
      if (val && val.show) {
        this.$refs.mfaModal.show();
      }
    },
    // when we close the alert here, update the global
    successDismissCountDown(val) {
      if (val <= 0) {
        this.$store.commit('clearGlobalAlert', false);
      }
    },
    errorDismissCountDown(val) {
      if (val <= 0) {
        this.$store.commit('clearGlobalAlert', true);
      }
    },
  },
  created() {
    // eslint-disable-next-line no-underscore-dangle
    const _failedToAuth = () => {
      this.$store.dispatch('clearUser');
      const currentPath = window.location.pathname;
      // eslint-disable-next-line global-require
      const pathConstants = require('@/constants/routes').default;

      // TODO: Come up with a better check for routes with params
      const isActivating = currentPath.indexOf('/confirm/') !== -1 || currentPath.indexOf('/reset/') !== -1;

      if (!isActivating
        && pathConstants.filter((p) => p.path === currentPath && p.allowAnonymous).length === 0) {
        return this.$router.push({name: 'login'});
      }
      return null;
    };

    if (StorageService.getItem('accessToken')) {
      this.userClient.getRemoteUser().then((user) => {
        if (user) {
          this.$store.commit('setUser', user);
          this.userClient.getUserMfaMethods().then((mfaResponse) => {
            this.$store.commit('setUserMfa', mfaResponse);
            if (this.$store.getters.mfaIsRequired) {
              this.$router.replace('/account');
            }
          });
          this.userClient.shouldShowTestFixture().then((showTestFixture) => {
            this.$store.commit('setShowTestFixture', showTestFixture);
          });
        } else {
          _failedToAuth();
        }
        this.$store.dispatch('loadedRemoteUser');
      }).catch(() => {
        this.$store.dispatch('loadedRemoteUser');
        _failedToAuth();
      });
    } else {
      this.$store.dispatch('loadedRemoteUser');
      _failedToAuth();
    }
  },
};
</script>

<style lang="scss">
@import "@/scss/styles.scss";

main {
  min-height: calc(100vh - (calc(#{$header-height} + 15px)));
  padding-top: calc(#{$header-height} + #{$gutter});
  background-color: $bg-color;
}

main:has(#redoc-container) {
  padding-top: $header-height !important;
}

.header-row {
  margin-bottom: 15px;

  h1 {
    font-size: 25px;

    @media (max-width: $sm-max) {
      margin-bottom: 20px;
    }
  }

  .custom-select {
    display: none;
    width: calc(100% - 200px);
    height: 42px;
    padding: 0 15px;
    vertical-align: top;
    cursor: pointer;

    &.full-width {
      width: 100%;
    }

    @media (min-width: $md-min) {
      position: relative;
      top: -5px;
      display: inline-block;
    }
  }

  .align-fix {
    div {
      @media (min-width: $md-min) {
        display: inline-block;
        float: right;
        vertical-align: top;
      }
    }
  }

  @media (max-width: $sm-max) {
    button {
      width: calc(100%);
      height: 50px;
      margin-bottom: env(safe-area-inset-bottom);
    }

    .form {
      width: 100%;
    }
  }

  @media (min-width: $md-min) {
    button {
      position: relative;
      top: -5px;
      float: right;
    }

    .org-header-bar .form {
      width: calc(100% - 350px);
    }

    .form {
      top: -13px;
      width: calc(100% - 150px);

      .form__input input.input {
        margin-bottom: 0;
      }
    }
  }

  .form {
    position: relative;
    display: inline-block;
  }
}

.no-results {
  @include border-radius($border-radius);

  height: 300px;
  margin-bottom: $gutter;
  background: white;
  border: solid 1px #ddd;
  border-top: solid 3px $red;
}
</style>
